import React, { useState } from 'react';
import { Mail, Phone, Facebook, Instagram, MapPin, Key } from 'lucide-react';
import axios from 'axios';
import SEO from './SEO';

interface FormData {
  name: string;
  email: string;
  message: string;
  phone?: string;
}

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: '',
    phone: ''
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });
  const [showApiInfo, setShowApiInfo] = useState(false);

  // Build-time injected via Vite (Docker ARG -> ENV) defaults to localhost:8000; direct backend access
  const apiBaseRaw = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || 'http://localhost:8000';
  const apiBase = apiBaseRaw.replace(/\/$/, '');

  // Get API key from environment
  const apiKey = (import.meta as { env?: { VITE_API_KEY?: string } }).env?.VITE_API_KEY || 'a4f7c832-6e9b-4d3a-8f2e-1b9c5d7e3f8a';

  const jsonTemplate = {
    name: 'John Doe',
    email: 'john@example.com',
    message: "I'm interested in selling my produce at your marketplace",
    phone: '+1 (555) 123-4567' // optional
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });
    try {
      await axios.post(`${apiBase}/contact`, formData, {
        headers: { 'X-API-Key': apiKey }
      });
      setStatus({ type: 'success', message: 'Thank you for your message. We will get back to you soon!' });
      setFormData({ name: '', email: '', message: '', phone: '' });
    } catch {
      setStatus({ type: 'error', message: 'There was an error sending your message. Please try again.' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.id]: e.target.value }));
  };

  // SEO structured data for contact page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Local Artist Fairs",
      "url": "https://localartistfairs.com",
      "email": "localartistfairs@gmail.com",
      "telephone": "(401) 594-0089",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Providence",
        "addressRegion": "RI",
        "postalCode": "02903",
        "addressCountry": "US"
      },
      "sameAs": [
        "https://facebook.com/localartistfairs",
        "https://instagram.com/localartistfairs"
      ]
    }
  };

  return (
    <>
      <SEO
        title="Contact Local Artist Fairs - Get in Touch"
        description="Contact Local Artist Fairs for questions about artist fairs, fresh produce, or to add your farm to our directory. Email localartistfairs@gmail.com or call (401) 594-0089."
        keywords="contact, local artist fairs, support, farm directory, farmers market contact, add artist fair"
        url="https://localartistfairs.com/contact"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-primary-dark mb-2">Contact Us</h1>
        <p className="text-gray-600">Get in touch with Local Artist Fairs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-primary-dark mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-secondary" />
              <a href="mailto:localartistfairs@gmail.com" className="text-gray-600 hover:text-secondary">
                localartistfairs@gmail.com
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-secondary" />
              <span className="text-gray-600">(401) 594-0089</span>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="h-5 w-5 text-secondary" />
              <span className="text-gray-600">Providence, RI 02903</span>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-medium text-primary-dark mb-3">Follow Us</h3>
            <div className="flex space-x-4">
              <a
                href="https://facebook.com/localartistfairs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-secondary transition-colors"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a
                href="https://instagram.com/localartistfairs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-600 transition-colors"
              >
                <Instagram className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* API Section */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-primary-dark mb-3">API Access</h3>
            <button
              onClick={() => setShowApiInfo(!showApiInfo)}
              className="flex items-center space-x-2 text-secondary hover:text-primary-dark"
            >
              <Key className="h-5 w-5" />
              <span>Request API Key</span>
            </button>
            {showApiInfo && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600 mb-2">
                  To request an API key, please email:
                </p>
                <a
                  href="mailto:localartistfairs@gmail.com"
                  className="text-secondary hover:text-primary-dark text-sm font-medium"
                >
                  localartistfairs@gmail.com
                </a>
                <div className="mt-3 border-t pt-3">
                  <p className="text-xs text-gray-500 mb-2">API Documentation:</p>
                  <pre className="text-xs overflow-x-auto">
                    {JSON.stringify(jsonTemplate, null, 2)}
                  </pre>
                  <p className="mt-2 text-xs text-gray-600">
                    Endpoint: POST /api/contact<br />
                    Required Header: X-API-Key
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-primary-dark mb-4">Send us a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {status.type && (
              <div className={`p-3 rounded-md ${status.type === 'success' ? 'bg-teal-100 text-primary-dark' : 'bg-red-100 text-red-700'}`}>
                {status.message}
              </div>
            )}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone (optional)
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-dark transition-colors"
            >
              Send Message
            </button>
          </form>
        </div>
      </div>
      </div>
    </>
  );
}